import { formatCurrency, isValidCardNumber } from '@chikara/utils';
import { CARD_NUMBER_LENGTH, SUPPORTED_CARD_BINS } from './activateCard.constants';
import { t } from '@/i18n';

// VALIDATORS
export const hasDisplayFullName = (value: string): boolean => Boolean(value.trim());

export const validateFullName = (value: string): string | undefined => {
    if (!value.trim()) {
        return t('dashboard.card.activateModal.errors.fullNameRequired');
    }
    return undefined;
};

export const isSupportedCardBinPrefix = (
    digits: string,
    bins: readonly string[] = SUPPORTED_CARD_BINS,
): boolean => {
    if (digits.length === 0) return true;
    return bins.some((bin) => bin.startsWith(digits) || digits.startsWith(bin));
};

const validateCardBinPrefix = (cleaned: string): string | undefined => {
    if (!isSupportedCardBinPrefix(cleaned)) {
        return t('dashboard.card.activateModal.errors.cardBinUnsupported');
    }
    return undefined;
};

export const validateCardNumber = (value: string): string | undefined => {
    const cleaned = value.replace(/[\s\-]/g, '');
    if (!cleaned) {
        return t('dashboard.card.activateModal.errors.cardNumberRequired');
    }
    const binError = validateCardBinPrefix(cleaned);
    if (binError) return binError;
    if (cleaned.length === CARD_NUMBER_LENGTH && !isValidCardNumber(cleaned)) {
        return t('dashboard.card.activateModal.errors.cardNumberInvalid');
    }
    return undefined;
};

export const validateConfirmCardNumber = (value: string, original: string): string | undefined => {
    const cleaned = value.replace(/[\s\-]/g, '');
    if (!cleaned) {
        return t('dashboard.card.activateModal.errors.confirmCardNumberRequired');
    }
    const binError = validateCardBinPrefix(cleaned);
    if (binError) return binError;
    const cardCleaned = original.replace(/[\s\-]/g, '');
    if (cleaned !== cardCleaned) {
        return t('dashboard.card.activateModal.errors.confirmCardNumberMismatch');
    }
    return undefined;
};

export const validateSpendingLimit = (value: string): string | undefined => {
    const numeric = value.replace(/\D/g, '');
    const numValue = Number(numeric) / 100;

    if (!value || isNaN(numValue) || numValue <= 0) {
        return t('dashboard.card.activateModal.errors.spendingLimitInvalid');
    }
    return undefined;
};

export const validateOnlyNumbers = (value: string): string => {
    return value.replace(/\D/g, '').slice(0, CARD_NUMBER_LENGTH);
};

// FORMATTERS
export const formatCardNumber = (value: string): string => {
    return value.replace(/(.{4})/g, '$1 ').trim();
};

export const formatCurrencyInput = (value: string): string => {
    const numeric = value.replace(/[^\d]/g, '');
    if (!numeric) return '';

    const number = Number(numeric) / 100;

    return formatCurrency(number);
};

export const lastFourDigits = (cardNumber: string): string => cardNumber.slice(-4);
